import { Divider, TextLink } from '@oneaudi/unified-web-components';
import { useCallback, useState } from 'react';
import { useI18n } from '~/app/i18n/i18n';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction } from '~/app/tracking/feld-m-types';
import { Dialog } from '~/components/dialog';
import { useConfigurator } from '~/graphql/hooks/use-configurator';
import { CatalogItemType } from '~/graphql/types';
import { MoreInformationDialogHeader } from '../more-information-dialog/components/more-information-dialog-header';
import { ModelTechnicalDataDialogContent } from './components/technical-data-dialog-content';

export function ModelTechnicalDataDialog({
	driveTrain,
	triggerText,
	trackSelection,
	step,
	total,
}: {
	driveTrain: CatalogItemType['driveTrain'];
	triggerText?: string;
	trackSelection?: () => void;
	step: number;
	total: number;
}) {
	const { i18n } = useI18n();
	const [open, setOpen] = useState(false);
	const vatConfig = useVATConfig();

	const { changeConfiguration } = useConfigurator();

	function onAddToConfiguration() {
		if (trackSelection) {
			trackSelection();
		}
		changeConfiguration(driveTrain);
		setOpen(false);
	}

	const onOpen = useCallback(
		(_open: boolean) => {
			appTrackingService.trackSelectionClickEvent({
				eventAction: EventAction.InternalLink,
				eventName: `selections - click to ${_open ? 'open' : 'close'} details layer`,
				label: i18n('ngc.actions.technicalData'),
				elementName: 'text link',
				value: 'technical data',
				category: 'engine',
				elementCurrentStep: step,
				elementTotalSteps: total,
				item: driveTrain,
			});
			setOpen(_open);
		},
		[driveTrain, i18n, setOpen, step, total],
	);

	return (
		<Dialog
			open={open}
			onCloseButtonClick={() => onOpen(false)}
			triggerElement={
				<TextLink variant={'standalone'} size={'sm'} onClick={() => onOpen(true)}>
					{i18n('ngc.actions.technicalData')}
				</TextLink>
			}>
			<MoreInformationDialogHeader
				vatConfig={vatConfig.trimlineAndEngine}
				headline={driveTrain.name}
				imageUrl={undefined}
				imageUrls={[]}
				price={driveTrain.totalPrice}
				rate={driveTrain.monthlyRate}
				status={driveTrain.status}
				onAddToConfiguration={onAddToConfiguration}
			/>
			<Divider />
			<ModelTechnicalDataDialogContent driveTrain={driveTrain} triggerQuery={open} />
		</Dialog>
	);
}
